import {
  B5,
  Box,
  Button,
  Card,
  Grid,
  Inline,
  P,
  Spinner,
  Stack
} from "@adfenix/alexandria";
import { getCustomTrigger } from "api/services/programService";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreateTriggerModal } from "./CreateTriggerModal";
import { RenameTriggerModal } from "./RenameTriggerModal";
import { ProgramService } from "@adfenix/types";
import { toast } from "react-toastify";
import Popover from "components/Popover";
import styled from "styled-components";
import { Timestamp } from "components/Timestamp";
import { AuditLogReadLayerModal } from "components/AuditLogReadLayer/Index";
import { useCheckPermission } from "hooks/usePermissions";
import { useRecoilValue } from "recoil";
import { userSelectedHq } from "atoms/auth";

export const availableTriggersQueryKey = "custom-trigger-events";

const B5HideMobile = styled(B5)`
  ${({ theme }) => theme.breakpoints.mobileAndLower} {
    display: none;
  }
`;

const PHideMobile = styled(P)`
  ${({ theme }) => theme.breakpoints.mobileAndLower} {
    display: none;
  }
`;

const copyToClipboard = (text: string) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied trigger id to clipboard");
  } catch (err) {
    toast.error(err as string);
  }
};

export const TriggerPage = () => {
  const selectedHq = useRecoilValue(userSelectedHq);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [triggerToRename, setTriggerToRename] =
    useState<ProgramService.CustomTriggerModel>();

  const hqId = selectedHq?.id;

  const { data } = useQuery(
    [availableTriggersQueryKey, hqId],
    () => getCustomTrigger({ hqId: hqId! }),
    {
      enabled: Boolean(hqId)
    }
  );

  const isAdmin = useCheckPermission({
    scope: "Admin",
    action: "View"
  });

  if (!data || !hqId) {
    return <Spinner centered />;
  }

  return (
    <>
      <Page
        header={
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <PageHeader title="Custom triggers" />
            <Button
              logLabel="create-new-trigger-button"
              size="medium"
              style={{ height: "40px" }}
              onClick={() => setShowCreateModal(true)}
            >
              Create new trigger
            </Button>
          </Box>
        }
      >
        <Stack spacing="gutter">
          <Card p="m" style={{ marginTop: "16px" }}>
            <Grid
              columns={[
                "1fr 90px",
                "minmax(auto, 350px) repeat(3, auto) 90px",
                "minmax(auto, 350px) repeat(3, auto) 90px"
              ]}
              gap="m"
            >
              <B5>Name</B5>
              <B5HideMobile>Subject type</B5HideMobile>
              <B5HideMobile>Last received</B5HideMobile>
              <B5HideMobile>Count</B5HideMobile>
              <B5></B5>

              {data.map(x => (
                <>
                  <P>{x.name}</P>
                  <PHideMobile>{x.targetSubjectType}</PHideMobile>
                  <PHideMobile>
                    <Timestamp timestamp={x.lastTriggered} />
                  </PHideMobile>
                  <PHideMobile>{x.receivedEvents}</PHideMobile>

                  <Inline justifyContent="end">
                    <Popover
                      trigger={
                        <Button
                          style={{ padding: 0 }}
                          variant="text"
                          logLabel="copy-trigger-button"
                          onClick={() => copyToClipboard(x.triggerId)}
                        >
                          <i className="uil uil-copy" />
                        </Button>
                      }
                    >
                      Copy trigger id
                    </Popover>

                    <Button
                      style={{ padding: 0 }}
                      variant="text"
                      onClick={() => setTriggerToRename(x)}
                      logLabel="rename-trigger-button"
                      data-testid={`rename-${x.triggerId}`}
                    >
                      <i className="uil uil-pen" />
                    </Button>
                    {isAdmin && (
                      <AuditLogReadLayerModal
                        buttonStyle={{ padding: 0 }}
                        correlationId={x.triggerId}
                        entities={["Property", "Agent", "Office"]}
                      >
                        <i className="uil uil-receipt-alt" />
                      </AuditLogReadLayerModal>
                    )}
                  </Inline>
                </>
              ))}
            </Grid>
          </Card>

          <Card>
            More information can be found through our{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://api.realforce.com/index.html"
            >
              API documentation
            </a>
            .
          </Card>
        </Stack>
      </Page>
      <CreateTriggerModal
        show={showCreateModal}
        setShow={setShowCreateModal}
        hqId={hqId}
      />
      {triggerToRename && (
        <RenameTriggerModal
          hqId={hqId}
          selectedTrigger={triggerToRename}
          resetSelectedTrigger={() => setTriggerToRename(undefined)}
        />
      )}
    </>
  );
};
